import { Component, type ReactNode } from 'react'
import * as Sentry from '@sentry/react'

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
  message: string
}

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, message: '' }

  static getDerivedStateFromError(err: unknown): State {
    const message = err instanceof Error ? err.message : String(err)
    return { hasError: true, message }
  }

  componentDidCatch(error: Error, info: { componentStack: string }) {
    Sentry.captureException(error, { contexts: { react: { componentStack: info.componentStack } } })
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            minHeight: '100dvh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '16px',
            padding: '32px',
            background: 'var(--paper)',
          }}
        >
          <p className="font-serif text-[22px] font-semibold text-green-900">
            Something went wrong
          </p>
          <p className="font-sans text-sm" style={{ color: 'var(--ink-3)', maxWidth: '400px', textAlign: 'center' }}>
            {this.state.message || 'An unexpected error occurred. Refresh the page to try again.'}
          </p>
          <button
            className="font-sans text-sm font-semibold"
            style={{
              padding: '8px 20px',
              borderRadius: '8px',
              background: 'var(--green-700)',
              color: '#fff',
              border: 'none',
              cursor: 'pointer',
            }}
            onClick={() => window.location.reload()}
          >
            Refresh
          </button>
        </div>
      )
    }
    return this.props.children
  }
}
